import Model, { attr } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { BookingStatus } from 'uplisting-frontend/models/booking';
import {
  OccasionChannel,
  OccasionType,
} from 'uplisting-frontend/models/occasion';
import { parseLocalDay } from 'uplisting-frontend/utils/date';

export default class StayModel extends Model {
  @service intl!: Services['intl'];
  @service translations!: Services['translations'];

  @attr('string') channel!: OccasionChannel;
  @attr('string') checkIn!: string | null;
  @attr('string') checkOut!: string | null;
  @attr('string') guestName!: string;
  @attr('string') multiUnitName!: string;
  @attr('number') numberOfGuests!: number;
  @attr('string') propertyNickname!: string;
  @attr('string') status!: BookingStatus;
  @attr('string') type!: OccasionType;
  @attr('string') uid!: string;

  // custom stuff needed for the search header
  pastDelimiter?: boolean;
  cancelledDelimiter?: boolean;

  @cached
  get channelName(): string {
    return this.translations.getChannelNameFor(this.channel);
  }

  @cached
  get humanType(): string {
    return this.intl.t(`action.occasion_search.human_type.${this.type}`);
  }

  @cached
  get checkInDate(): Date {
    return parseLocalDay(this.checkIn);
  }

  @cached
  get checkOutDate(): Date {
    return parseLocalDay(this.checkOut);
  }

  @cached
  get isInPast(): boolean {
    if (!this.checkIn || !this.checkOut) {
      return String(this.status) === 'expired';
    }

    const { checkOutDate } = this;
    const today = new Date();

    return isBefore(checkOutDate, today) && !isSameDay(checkOutDate, today);
  }

  @cached
  get isOngoing(): boolean {
    if (!this.checkIn || !this.checkOut) {
      return String(this.status) !== 'expired';
    }

    const { checkInDate, checkOutDate } = this;
    const today = new Date();

    return (
      (isSameDay(checkInDate, today) || isBefore(checkInDate, today)) &&
      (isSameDay(checkOutDate, today) || isAfter(checkOutDate, today))
    );
  }

  @cached
  get isInFuture(): boolean {
    if (!this.checkIn) {
      return false;
    }

    return isAfter(this.checkInDate, new Date());
  }

  @cached
  get isCancelled(): boolean {
    return this.status === 'cancelled';
  }

  @cached
  get isDateless(): boolean {
    return !this.checkIn || !this.checkOut;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    stay: StayModel;
  }
}
